import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import LearningPathdetailsCard from "../../components/cards/LearningPathdetailsCard";
import HeaderPage from "../../components/HeaderPage";
import Modal from "../../components/Modals/Modal";
import { useGetTrackQuery } from "../../services/users/learningTrack";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Icon from "../../components/icons/Icon";
import { ShareIcon } from "../../components/icons/Icons";
import NotFoundPage from "../404";

const PATH_DETAILS_EXAM_MODAL_ID = "path-details-exam-complete-courses-modal";

let courseStatuses = (progress, t) => [
  {
    name: "inprogress",
    buttonText: progress
      ? t("learningPaths:continueCourse")
      : t("learningPaths:startCourse"),
    buttonIcon: "fas fa-play-circle fa-fw me-3",
  },
  {
    name: "closed",
    buttonText: t("learningPaths:closedCourse"),
    buttonSubText: t("learningPaths:coursePrerequistes"),
    buttonIcon: "fas fa-lock fa-fw",
    btnDisabled: true,
  },
  {
    name: "completed",
    buttonText: t("learningPaths:displayCourseCertificate"),
    showCertificate: true,
  },
];

const LearningPathDetails = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const { data: learningTrack, isLoading: isLearningTrackLoading } =
    useGetTrackQuery(id);

  let ProgressBarPercentage = `${learningTrack?.data?.enrollment?.progress}%`;
  let completeDate = moment(
    new Date(learningTrack?.data?.enrollment?.complete_by),
  );
  let oneDay = 24 * 60 * 60 * 1000;
  let remainingDays = Math.round((completeDate - new Date()) / oneDay);
  let absRemainingDays = Math.abs(remainingDays);
  const { t, i18n } = useTranslation(["common", "learningPaths"]);
  let subDetails =
    t("learningPaths:learningPathsDeadline", {
      date: completeDate.locale(i18n.language).format("D MMM YYYY"),
    }) +
    " " +
    (remainingDays > 0
      ? t("learningPaths:remainingTime", {
          days: remainingDays,
        })
      : t("learningPaths:deadlinePassed", { lateDays: absRemainingDays }));

  let isOneCourseOpened = false;

  const courses = learningTrack?.data?.courses;
  const allPathCoursesCompleted =
    !courses?.length || courses.every((c) => c?.enrollment?.progress == 100);

  const enrollment = learningTrack?.data?.enrollment;
  const attempts = enrollment?.attempts ?? 0;
  const status = enrollment?.status ?? null;
  const nextAttemptTime = enrollment?.next_attempt_time;
  const canRetakeNow =
    allPathCoursesCompleted &&
    attempts === 1 &&
    status === "failed" &&
    nextAttemptTime &&
    new Date() >= new Date(nextAttemptTime);

  const [showCompleteCoursesModal, setShowCompleteCoursesModal] =
    useState(false);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (!nextAttemptTime || canRetakeNow) {
      setCountdown("");
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const target = new Date(nextAttemptTime);
      const diff = target - now;

      if (diff <= 0) {
        setCountdown("");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextAttemptTime, canRetakeNow]);

  useEffect(() => {
    const el = document.getElementById(PATH_DETAILS_EXAM_MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (showCompleteCoursesModal) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [showCompleteCoursesModal]);

  useEffect(() => {
    const el = document.getElementById(PATH_DETAILS_EXAM_MODAL_ID);
    if (!el) return;
    const handleHidden = () => setShowCompleteCoursesModal(false);
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, []);

  const handleSharePathCertificate = async () => {
    const url = enrollment?.certificate_url;
    if (!url) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: learningTrack?.data?.title,
          text: t("learningPaths:shareCertificate"),
          url,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success(t("learningPaths:certificateLinkCopied"));
      } else {
        window.prompt("Copy this link:", url);
      }
    } catch (error) {
      // user dismissed native share
    }
  };

  if (isLearningTrackLoading) return <LoadingIndicator />;

  if (!learningTrack) {
    return <NotFoundPage />;
  }

  const renderPathExamActions = () => {
    const btnClass =
      "btn btn-primary btn-lg px-5 py-2 employee-learning-path-details__exam-btn";

    if (allPathCoursesCompleted && attempts === 0) {
      return (
        <button
          type="button"
          className={btnClass}
          onClick={() => navigate(`/employee/learning_paths/${id}/exam`)}
        >
          {t("learningPaths:startPathExam")}
        </button>
      );
    }

    if (!allPathCoursesCompleted) {
      return (
        <button
          type="button"
          className={btnClass}
          onClick={() => setShowCompleteCoursesModal(true)}
        >
          {t("learningPaths:startPathExam")}
        </button>
      );
    }

    if (allPathCoursesCompleted && attempts === 1 && status === "passed") {
      return (
        <div className="d-flex flex-row gap-2 align-items-stretch justify-content-center flex-wrap align-self-center w-100">
          <button
            type="button"
            className={btnClass}
            onClick={() =>
              enrollment?.certificate_url &&
              window.open(enrollment.certificate_url, "_blank")
            }
            disabled={!enrollment?.certificate_url}
          >
            {t("learningPaths:pathExamDownloadCertificate")}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-lg px-4 py-2 d-inline-flex align-items-center justify-content-center text-dark employee-learning-path-details__exam-btn"
            onClick={handleSharePathCertificate}
            disabled={!enrollment?.certificate_url}
          >
            <span className="me-2 d-inline-flex align-middle">
              <Icon
                icon={<ShareIcon />}
                iconName="share"
                fill="currentColor"
                iconSize={18}
                height={15.48}
                viewBox="0 0 18 18"
              />
            </span>
            {t("learningPaths:shareCertificate")}
          </button>
        </div>
      );
    }

    if (allPathCoursesCompleted && attempts === 1 && status === "failed") {
      return (
        <>
          <button
            type="button"
            className={btnClass}
            onClick={() =>
              canRetakeNow && navigate(`/employee/learning_paths/${id}/exam`)
            }
            disabled={!canRetakeNow}
          >
            {t("learningPaths:pathExamRetakeExam")}
          </button>
          {!canRetakeNow && nextAttemptTime && (
            <p className="font_14 font-color-grey mt-2 mb-0">
              {countdown
                ? t("learningPaths:pathExamRetakeAvailableAfter", {
                    datetime: countdown,
                  })
                : t("learningPaths:pathExamRetakeAvailableAfter", {
                    datetime:
                      moment(nextAttemptTime).format("DD/MM/YYYY HH:mm"),
                  })}
            </p>
          )}
        </>
      );
    }

    if (allPathCoursesCompleted && attempts > 1 && status === "failed") {
      return (
        <p className="font_20 font-color-red mb-0">
          {t("learningPaths:pathExamDidNotPass")}
        </p>
      );
    }

    return (
      <button
        type="button"
        className={btnClass}
        onClick={() =>
          allPathCoursesCompleted
            ? navigate(`/employee/learning_paths/${id}/exam`)
            : setShowCompleteCoursesModal(true)
        }
      >
        {t("learningPaths:startPathExam")}
      </button>
    );
  };

  return (
    <div className="employee-learning-path-details">
      {learningTrack?.data ? (
        <HeaderPage
          progressBarFullDetails={t("learningPaths:completedPortion")}
          progressBarPercentageText={ProgressBarPercentage}
          progressBarSubDetails={subDetails}
          sectionTitle={learningTrack?.data?.title}
          listItems={[
            {
              name: t("learningPaths"),
              href: "/employee/learning_paths",
            },
            {
              name: learningTrack?.data?.title,
              href: "#",
            },
          ]}
        />
      ) : null}
      <div className="container-fluid">
        <div className="row justify-content-center p-4">
          <div className="col-lg-10 col-md-12 employee-learning-path-details__body">
            <ul className="list-progressbar-cards">
              {learningTrack?.data?.courses?.map((course, i) => {
                let courseStatusName = "inprogress";
                if (course?.enrollment?.progress == 100)
                  courseStatusName = "completed";
                else if (!isOneCourseOpened) {
                  courseStatusName = "inprogress";
                  isOneCourseOpened = true;
                }

                let courseStatus = courseStatuses(
                  course?.enrollment?.progress,
                  t,
                ).find((status) => status.name == courseStatusName);

                return (
                  <li
                    key={course?.id}
                    className={`mb-4
                      ${courseStatus?.name == "completed" ? "completed" : null}
                      `}
                  >
                    <hr className="border-dashed" />
                    <span className="list-progressbar-cards-state">
                      {i + 1}
                    </span>
                    <LearningPathdetailsCard
                      course={course}
                      courseStatus={courseStatus}
                      state={{
                        ProgressBarPercentage,
                        learningPathName: learningTrack?.data?.title,
                        learningPathId: learningTrack?.data?.id,
                      }}
                      courseStatusClassName="text-start"
                    />
                  </li>
                );
              })}
            </ul>
            {learningTrack?.data?.questions?.length > 0 && (
              <div className="d-flex flex-column align-items-center gap-2 mt-4 employee-learning-path-details__exam-actions">
                {renderPathExamActions()}
              </div>
            )}
            <Modal
              id={PATH_DETAILS_EXAM_MODAL_ID}
              title={t("learningPaths:pathExamTitle")}
              isOpened={showCompleteCoursesModal}
              onClose={() => setShowCompleteCoursesModal(false)}
              hasCloseBtn
              mainBtn={t("learningPaths:pathExamModalClose")}
              mainBtnOnclick={() => setShowCompleteCoursesModal(false)}
              maxWidth={500}
              isVerticallyCentered
            >
              <p className="font_16 font-color-grey mb-0">
                {t("learningPaths:pathExamCompleteCoursesFirst")}
              </p>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPathDetails;
